<template>
  <div class="exp-slider-skeleton">
    <PageWrap>
      <SkeletonItem
        class="exp-slider-skeleton__title"
        width="360px"
        :border-radius="8"
        :is-animated="animated"
        ignore-global-animation
      />
    </PageWrap>

    <div class="exp-slider-skeleton__viewport">
      <div class="exp-slider-skeleton__row">
        <!-- Разметка и размеры повторяют ExpCardRedesignSkeleton из exp-card, но без импорта пакета (он тяжёлый) -->
        <div
          v-for="n in 6"
          :key="n"
          class="exp-slider-skeleton__card"
        >
          <SkeletonItem
            class="exp-slider-skeleton__image"
            :is-animated="animated"
            ignore-global-animation
          />
          <div class="exp-slider-skeleton__rating">
            <div class="exp-slider-skeleton__guide-info">
              <SkeletonItem
                class="exp-slider-skeleton__avatar"
                :is-animated="animated"
                ignore-global-animation
              />
              <SkeletonItem
                class="exp-slider-skeleton__guide"
                :is-animated="animated"
                ignore-global-animation
              />
            </div>
            <SkeletonItem
              class="exp-slider-skeleton__stars"
              :is-animated="animated"
              ignore-global-animation
            />
          </div>
          <div class="exp-slider-skeleton__content">
            <SkeletonItem
              class="exp-slider-skeleton__line"
              :is-animated="animated"
              ignore-global-animation
            />
            <SkeletonItem
              class="exp-slider-skeleton__line exp-slider-skeleton__line--medium"
              :is-animated="animated"
              ignore-global-animation
            />
          </div>
          <div class="exp-slider-skeleton__content">
            <SkeletonItem
              class="exp-slider-skeleton__line exp-slider-skeleton__line--small exp-slider-skeleton__line--long"
              :is-animated="animated"
              ignore-global-animation
            />
            <SkeletonItem
              class="exp-slider-skeleton__line exp-slider-skeleton__line--small exp-slider-skeleton__line--short"
              :is-animated="animated"
              ignore-global-animation
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Место под кнопку «Смотреть все» -->
    <PageWrap>
      <SkeletonItem
        class="exp-slider-skeleton__button"
        :border-radius="12"
        :is-animated="animated"
        ignore-global-animation
      />
    </PageWrap>
  </div>
</template>

<script setup lang="ts">
  import { SkeletonItem } from '@experience/core-skeleton-lib';

  import PageWrap from '@/shared/ui/PageWrap.vue';

  withDefaults(
    defineProps<{
      animated?: boolean;
    }>(),
    {
      animated: true,
    },
  );
</script>

<style scoped lang="scss">
  // Размеры подогнаны под реальный блок ExperienceSlider (замерено): отступы, заголовок,
  // ширина карточек по breakpoints слайдера (см. ExpSlider.vue) и кнопка «Смотреть все». Так нет сдвига при замене.
  .exp-slider-skeleton {
    --off: 20px;
    --n: 4;

    padding-top: 40px;

    &__title {
      height: 32px;
      margin-bottom: 24px;

      @include mobile {
        height: 28px;
      }
    }

    &__viewport {
      box-sizing: border-box;
      width: 100vw;
      min-width: var(--wrap-min-width);
      max-width: var(--wrap-max-width);
      padding: 0 var(--off);
      margin: 0 auto;
    }

    &__row {
      display: flex;
      gap: 16px;
      overflow: hidden;
    }

    &__card {
      display: flex;
      flex: 0 0 calc((100% - (var(--n) - 1) * 16px) / var(--n));
      flex-direction: column;
      min-width: 0;
    }

    &__image {
      width: 100%;
      aspect-ratio: 287 / 187;
      border-radius: var(--alias-radius-12, 12px);
    }

    &__rating {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 0;
    }

    &__guide-info {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    &__avatar {
      width: 24px;
      height: 24px;
      border-radius: 50%;
    }

    &__guide {
      width: 72px;
      height: 14px;
      border-radius: 8px;
    }

    &__stars {
      width: 140px;
      height: 14px;
      border-radius: 8px;
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-top: 8px;
    }

    &__line {
      width: 100%;
      height: 20px;
      border-radius: 8px;

      &--medium {
        width: 85%;
      }

      &--long {
        width: 90%;
      }

      &--short {
        width: 60%;
      }

      &--small {
        height: 18px;
      }
    }

    &__button {
      width: 160px;
      height: 50px;
      margin-top: 24px;

      @include tablet {
        width: 100%;
      }
    }

    @media (width <= 1080px) {
      --n: 3;
    }

    @media (width <= 920px) {
      --n: 2.5;
    }

    @media (width <= 800px) {
      --n: 2;
    }

    @media (width <= 600px) {
      --n: 1.5;
    }

    @media (width <= 480px) {
      --n: 1.25;
    }

    @media (width <= 400px) {
      --n: 1.15;
    }

    @media (width <= 370px) {
      --n: 1;
    }

    @include mobile {
      --off: 24px;

      padding-top: 32px;
    }
  }
</style>
